import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { buildLearningTopicPath } from '../../lib/educationContent';
import { completeLearningAssignment, fetchAssignedLearning, setTrackItemCompletion } from '../../lib/assignedLearning';

const statusStyles = {
  assigned: 'bg-blue-50 text-blue-700 border-blue-100',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-100',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  dismissed: 'bg-slate-100 text-slate-500 border-slate-200'
};

const typeLabels = {
  topic: 'Topic',
  resource: 'Resource',
  track: 'Track'
};

const getAssignmentTitle = (assignment) => {
  if (assignment.assignment_type === 'topic') return assignment.topic?.title || 'Learning Topic';
  if (assignment.assignment_type === 'resource') return assignment.resource?.title || 'Learning Resource';
  return assignment.track?.title || 'Learning Track';
};

const getAssignmentDescription = (assignment) => {
  if (assignment.assignment_type === 'topic') {
    return assignment.topic?.short_summary || assignment.topic?.plain_english_explanation;
  }
  if (assignment.assignment_type === 'resource') return assignment.resource?.short_description;
  return assignment.track?.description;
};

const AssignedLearningPanel = ({
  matterIds = [],
  matters = [],
  title = 'Assigned Learning',
  emptyText = 'Assigned learning from your advisor or attorney will appear here.'
}) => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const matterKey = useMemo(() => matterIds.filter(Boolean).join('|'), [matterIds]);

  const matterById = useMemo(() => (
    matters.reduce((map, matter) => {
      map[matter.id] = matter;
      return map;
    }, {})
  ), [matters]);

  useEffect(() => {
    const loadAssignments = async () => {
      try {
        setLoading(true);
        setError(null);
        setAssignments(await fetchAssignedLearning({ matterIds }));
      } catch (err) {
        console.error('Assigned learning load error:', err);
        setError(err.message || 'Unable to load assigned learning.');
      } finally {
        setLoading(false);
      }
    };

    loadAssignments();
  }, [matterKey]);

  const openAssignment = (assignment) => {
    if (assignment.assignment_type === 'topic' && assignment.topic?.slug) {
      navigate(buildLearningTopicPath({ slug: assignment.topic.slug, matterId: assignment.matter_id }));
      return;
    }

    if (assignment.assignment_type === 'resource' && assignment.resource?.slug) {
      navigate(`/education/${assignment.resource.slug}?matter=${assignment.matter_id}`);
    }
  };

  const openTrackItem = (assignment, item) => {
    if (item.topic?.slug) {
      navigate(buildLearningTopicPath({ slug: item.topic.slug, matterId: assignment.matter_id }));
      return;
    }

    if (item.resource?.slug) {
      navigate(`/education/${item.resource.slug}?matter=${assignment.matter_id}`);
    }
  };

  const markComplete = async (assignment) => {
    try {
      setSavingId(assignment.id);
      const updated = await completeLearningAssignment(assignment.id);
      setAssignments((current) => current.map((item) => (
        item.id === assignment.id ? { ...item, ...updated, track_items: item.track_items } : item
      )));
    } catch (err) {
      console.error('Complete learning assignment error:', err);
      setError(err.message || 'Unable to mark assignment complete.');
    } finally {
      setSavingId(null);
    }
  };

  const toggleTrackItemComplete = async (assignment, item) => {
    try {
      const savingKey = `${assignment.id}:${item.id}`;
      setSavingId(savingKey);
      const updatedAssignment = await setTrackItemCompletion({
        assignmentId: assignment.id,
        trackItemId: item.id,
        completed: !item.completed
      });

      setAssignments((current) => current.map((currentAssignment) => {
        if (currentAssignment.id !== assignment.id) return currentAssignment;

        const trackItems = currentAssignment.track_items.map((trackItem) => (
          trackItem.id === item.id ? { ...trackItem, completed: !item.completed } : trackItem
        ));

        return {
          ...currentAssignment,
          ...updatedAssignment,
          topic: currentAssignment.topic,
          resource: currentAssignment.resource,
          track: currentAssignment.track,
          track_items: trackItems,
          total_item_count: trackItems.length,
          completed_item_count: trackItems.filter((trackItem) => trackItem.completed).length
        };
      }));
    } catch (err) {
      console.error('Track item completion error:', err);
      setError(err.message || 'Unable to update track item.');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center text-sm text-slate-500">
          <SafeIcon icon={FiIcons.FiLoader} className="mr-2 h-4 w-4 animate-spin text-slate-300" />
          Loading assigned learning...
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-6 py-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{title}</h2>
            <p className="mt-1 text-xs text-slate-500">Learning assigned by your planning team.</p>
          </div>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold uppercase tracking-widest text-slate-500">
            {assignments.length} Assigned
          </span>
        </div>
      </div>

      <div className="p-6">
        {error && (
          <div className="mb-4 rounded-md border-l-4 border-red-400 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {assignments.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-6 text-center">
            <SafeIcon icon={FiIcons.FiBookOpen} className="mx-auto mb-3 h-8 w-8 text-slate-300" />
            <p className="text-sm text-slate-500">{emptyText}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {assignments.map((assignment) => {
              const matter = matterById[assignment.matter_id];
              const complete = assignment.status === 'completed';

              return (
                <article key={assignment.id} className="rounded-lg border border-slate-200 p-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className="rounded bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-blue-700">
                          {typeLabels[assignment.assignment_type]}
                        </span>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${statusStyles[assignment.status] || statusStyles.assigned}`}>
                          {assignment.status.replaceAll('_', ' ')}
                        </span>
                        {matter?.plan_type && (
                          <span className="rounded bg-slate-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                            {matter.plan_type}
                          </span>
                        )}
                      </div>
                      <h3 className="text-base font-bold text-slate-900">{getAssignmentTitle(assignment)}</h3>
                      {getAssignmentDescription(assignment) && (
                        <p className="mt-2 text-sm leading-relaxed text-slate-600">{getAssignmentDescription(assignment)}</p>
                      )}
                      {assignment.note && (
                        <p className="mt-3 rounded-md border border-blue-100 bg-blue-50 p-3 text-sm italic text-blue-800">
                          {assignment.note}
                        </p>
                      )}
                      <p className="mt-3 text-xs text-slate-400">
                        Assigned {format(new Date(assignment.created_at), 'MMM d, yyyy')}
                        {assignment.due_date && ` - Due ${format(new Date(`${assignment.due_date}T00:00:00`), 'MMM d, yyyy')}`}
                      </p>
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row lg:flex-col">
                      {assignment.assignment_type !== 'track' && (
                        <button
                          type="button"
                          onClick={() => openAssignment(assignment)}
                          className="inline-flex items-center justify-center rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900"
                        >
                          Open
                          <SafeIcon icon={FiIcons.FiArrowRight} className="ml-2 h-4 w-4" />
                        </button>
                      )}
                      {!complete && assignment.assignment_type !== 'track' && (
                        <button
                          type="button"
                          onClick={() => markComplete(assignment)}
                          disabled={savingId === assignment.id}
                          className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        >
                          <SafeIcon icon={savingId === assignment.id ? FiIcons.FiLoader : FiIcons.FiCheckCircle} className={`mr-2 h-4 w-4 ${savingId === assignment.id ? 'animate-spin' : ''}`} />
                          Mark Complete
                        </button>
                      )}
                    </div>
                  </div>

                  {assignment.assignment_type === 'track' && assignment.track_items.length > 0 && (
                    <div className="mt-5 rounded-lg border border-slate-100 bg-slate-50/60 p-4">
                      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Track Items</p>
                        <span className="text-xs font-semibold text-slate-500">
                          {assignment.completed_item_count} of {assignment.total_item_count} complete
                        </span>
                      </div>
                      <div className="mb-4 h-2 overflow-hidden rounded-full bg-slate-200">
                        <div
                          className="h-2 rounded-full bg-emerald-500 transition-all duration-500"
                          style={{
                            width: `${assignment.total_item_count > 0
                              ? Math.round((assignment.completed_item_count / assignment.total_item_count) * 100)
                              : 0}%`
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        {assignment.track_items.map((item, index) => {
                          const itemTitle = item.title_override || item.topic?.title || item.resource?.title || 'Learning Item';
                          const savingKey = `${assignment.id}:${item.id}`;
                          return (
                            <div key={item.id} className="flex flex-col gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                              <button
                                type="button"
                                onClick={() => openTrackItem(assignment, item)}
                                className="flex min-w-0 flex-1 items-center text-left text-sm hover:text-blue-700"
                              >
                                <SafeIcon
                                  icon={item.completed ? FiIcons.FiCheckCircle : FiIcons.FiCircle}
                                  className={`mr-2 h-4 w-4 shrink-0 ${item.completed ? 'text-emerald-600' : 'text-slate-300'}`}
                                />
                                <span className={`font-medium ${item.completed ? 'text-slate-500' : 'text-slate-800'}`}>
                                  {index + 1}. {itemTitle}
                                </span>
                              </button>
                              <button
                                type="button"
                                onClick={() => toggleTrackItemComplete(assignment, item)}
                                disabled={savingId === savingKey}
                                className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-slate-600 hover:bg-slate-100 disabled:opacity-50"
                              >
                                <SafeIcon
                                  icon={savingId === savingKey ? FiIcons.FiLoader : item.completed ? FiIcons.FiRotateCcw : FiIcons.FiCheck}
                                  className={`mr-2 h-3.5 w-3.5 ${savingId === savingKey ? 'animate-spin' : ''}`}
                                />
                                {item.completed ? 'Undo' : 'Done'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default AssignedLearningPanel;
